import * as React from 'react';
import { Icon, IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { IComponentAs } from '@uifabric/utilities/lib/IComponentAs';
import { getFileTypeIconProps, FileIconType, IFileTypeIconOptions } from '@uifabric/file-type-icons/lib/index';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { fileNameFacet, folderFacet } from '../../facets/ItemFacets';
import { fileIconTypeFacet, oneNoteFacet } from '../../facets/DocumentFacets';
import { itemFileTypeIconControl } from '../../controls/itemFileTypeIconControl';
import { getBrandTypeIconProps } from '../brandIcon/brandIconHelper';

const ONENOTE_EXTENSION = 'one';

/**
 * @internal
 */
export interface IFileTypeIconProps extends IFileTypeIconOptions {
  className?: string;
  isOneNote?: boolean;
  isFolder?: boolean;
  iconAs?: IComponentAs<IIconProps>;
  preferAppIcon?: boolean;
}

/**
 * @internal
 */
export const FileTypeIcon: React.StatelessComponent<IFileTypeIconProps> = (props: IFileTypeIconProps) => {
  const {
    extension,
    type,
    className,
    isOneNote,
    size,
    isFolder,
    preferAppIcon = false
  } = props;

  const IconAs: IComponentAs<IIconProps> = props.iconAs || Icon;
  const ext = isOneNote ? ONENOTE_EXTENSION : extension;
  return (
    <IconAs
      defaultRender={ Icon }
      className={ className }
      { ...preferAppIcon && ext ? getBrandTypeIconProps({
        size: size,
        extension: ext
      }) : getFileTypeIconProps({
        ...(extension || type || isOneNote || isFolder ? {
          extension: ext,
          type: type !== undefined ? type :
            (isFolder ? 2 as FileIconType.folder : undefined)
        } : {
            type: 3 as FileIconType.genericFile
          }),
        size: size
      }) }
    />
  );
};

/**
 * @beta
 */
export interface IItemFileTypeIconProps extends IFileTypeIconProps {
  itemKey: string | undefined;
}

/**
 * @beta
 */
export const ItemFileTypeIcon: React.ComponentType<IItemFileTypeIconProps> = connectToItemCache((
  itemCache: IItemCache,
  props: IItemFileTypeIconProps
): Pick<IFileTypeIconProps, 'extension' | 'type' | 'isOneNote' | 'isFolder' | 'iconAs' | 'size' | 'preferAppIcon'> => {
  const {
    itemKey,
    size,
    preferAppIcon
  } = props;

  const {
    fileName,
    folder,
    fileIconType,
    oneNote,
    controlHandler
  } = itemCache.demandItem({
    fileName: fileNameFacet,
    folder: folderFacet,
    fileIconType: fileIconTypeFacet,
    oneNote: oneNoteFacet,
    controlHandler: itemFileTypeIconControl
  }, itemKey);

  const extension = fileName && fileName.extension;

  const {
    iconAs
  } = itemFileTypeIconControl.evaluate(controlHandler)(itemCache, {
    itemKey,
    iconAs: Icon
  });

  return {
    extension,
    isFolder: !!folder,
    type: fileIconType,
    isOneNote: !!oneNote,
    iconAs,
    size,
    preferAppIcon
  };
})(FileTypeIcon);
